import { PlusOutlined } from "@ant-design/icons";
import * as Sentry from "@sentry/browser";
import { Button, Table, Tag, message } from "antd";
import { AxiosResponse } from "axios";
import debounce from "lodash.debounce";
import React from "react";
import { RouteComponentProps } from "react-router";
import { NavLink } from "react-router-dom";
import AdvancedSearchModal, {
  AdvancedSearchReturn,
  IField,
} from "../components/AdvancedSearchModal";
import Card from "../components/Card";
import EntitledSection from "../components/EntitledSection";
import ExportData from "../components/ExportData";
import { PageToolbar } from "../components/PageToolbar";
import entitlements from "../config/entitlements";
import { Product } from "../helpers/shared-interfaces";
import {
  generateDisplayErrorMessage,
  request,
  responseHandler,
} from "../helpers/utilities";
import { GET_PRODUCTS_INCLUDING_PHASED_OUT } from "../gql/products";
import { SYNC_PRICES } from "../gql/general";
import { DEBOUNCE_TIME } from "../helpers/constants";


interface State {
  isFiltering: boolean;
  products: Product[];
  page: number;
  productCount: number;
  filteredProducts: Product[];
  filterBy: string;
  isLoading: boolean;
  showDialog: boolean;
  showAdvancedSearchModal: boolean;
  selectedFieldsToDisplay: IField[];
  removedField?: IField;
}

export const columns = [
  {
    title: "Product",
    dataIndex: "name",
    key: "Product",
    width: "50%",
    render(text: string, record: Product): JSX.Element {
      return (
        <span>
          <NavLink
            to={`/products/${record.id}`}
            data-cy="test-product-details-link"
          >
            {text}
          </NavLink>
          {record.isActive === false && (
            <Tag color="orange" style={{ marginLeft: "8px" }}>
              Phased out
            </Tag>
          )}
        </span>
      );
    },
  },
  {
    title: "Category",
    dataIndex: "productCategory",
    key: "Category",
    width: "40%",
    render(text: string, record: Product): JSX.Element {
      return <span>{record.productCategory.name}</span>;
    },
  },
  {
    title: "Prospect",
    dataIndex: "opportunityProspect",
    key: "Prospect",
    width: "10%",
    render(text: string, record: Product): JSX.Element {
      return (
        <span>
          {record.opportunityProspect && record.opportunityProspect.name}
        </span>
      );
    },
  },
];

export function ProductTableFactory(
  data: Product[],
  isLoading: boolean,
  count: number,
  page: number,
  nextPage: (page: number) => void,
): JSX.Element {
  const tempData: Product[] = data.map((product: Product): Product => {
    return { ...product, key: product.id };
  });
  return (
    <Table
      loading={isLoading}
      scroll={{ x: 500 }}
      columns={columns}
      dataSource={tempData}
      pagination={{
        current: page,
        defaultPageSize: 25,
        total: count,
        onChange: nextPage,
      }}
    />
  );
}

export default class AllProducts extends React.Component<
  RouteComponentProps,
  State
> {
  constructor(props: RouteComponentProps) {
    super(props);
    this.state = {
      showDialog: false,
      filterBy: "",
      isLoading: true,
      products: [],
      page: 1,
      productCount: 0,
      filteredProducts: [],
      isFiltering: false,
      showAdvancedSearchModal: false,
      selectedFieldsToDisplay: [],
    };

    this.redirectToNewProductPage = this.redirectToNewProductPage.bind(this);
    this.getAllProducts = this.getAllProducts.bind(this);
    this.filterProducts = this.filterProducts.bind(this);
    this.nextPage = this.nextPage.bind(this);
    this.syncPrices = this.syncPrices.bind(this);
  }

  async filterProducts(e: React.ChangeEvent<HTMLInputElement>): Promise<void> {
    e.persist();
    this.setState({ filterBy: e.target.value, isLoading: true });
    this.triggerDelayedSearch();
  }

  triggerDelayedSearch = debounce(async (): Promise<void> => {
    this.getAllProducts();
  }, DEBOUNCE_TIME);

  getAllProducts(): void {
    const { page, filterBy } = this.state;
    this.setState({ isLoading: true });
    request({
      query: GET_PRODUCTS_INCLUDING_PHASED_OUT,
      variables: {
        page,
        q: filterBy,
      },
    })
      .then(({ data: { data } }: any): void => {
        this.setState({
          products: data.products.results,
          productCount: data.products.count,
        });
      })
      .catch((error) => {
        message.error("Failed to load products");
      })
      .finally(() => {
        this.setState({ isLoading: false });
      });
  }

  toggleAdvancedSearchModel = (): void => {
    const prevState = this.state.showAdvancedSearchModal;
    this.setState({ showAdvancedSearchModal: !prevState });
  };

  redirectToNewProductPage(): void {
    this.props.history.push("/products/new");
  }

  handleOnAdvancedSearchData = (
    data: AdvancedSearchReturn,
    fields: IField[],
  ) => {
    this.setState({
      products: data.results as Product[],
      selectedFieldsToDisplay: fields,
      page: 1,
      productCount: data.count,
    });
  };

  handleOnAdvancedSearchError = (error: string) => {
    message.error("Failed to execute query");
    Sentry.captureException(error);
  };

  handleOnFieldRemove = (field: IField) => {
    const fields = this.state.selectedFieldsToDisplay.filter(
      (f) => f.name !== field.name,
    );
    this.setState({
      removedField: field,
      selectedFieldsToDisplay: fields,
    });

    if (fields.length === 0) this.getAllProducts();
  };

  async syncPrices(): Promise<void> {
    interface Response {
      data: {
        syncPricesManually: {
          result: boolean;
        };
      };
    }

    this.setState({
      isLoading: true,
    });

    try {
      const response: AxiosResponse<Response> = await request(
        {
          query: SYNC_PRICES,
        },
        { timeout: 30000 },
      );

      const { err } = responseHandler<Response>(response, "syncPricesManually");
      if (err) {
        message.error(generateDisplayErrorMessage(err));
      } else {
        const result: boolean = response.data.data.syncPricesManually.result;
        result
          ? message.success("Sync in progress")
          : message.error(`Failed to sync prices - ${generateDisplayErrorMessage(err)}`);
      }
    } catch (error) {
      if (error.code === "ECONNABORTED") {
        message.error(`Price Sync In Progress - ${generateDisplayErrorMessage(error)}`);
      } else {
        message.error(`Failed to sync prices - ${generateDisplayErrorMessage(error)}`);
        Sentry.captureException(error);
      }
    } finally {
      this.setState({
        isLoading: false,
      });
    }
  }

  componentDidMount(): void {
    this.setState({ isLoading: true }, (): void => {
      this.getAllProducts();
    });
  }

  nextPage(page: number) {
    this.setState({ isLoading: true, page }, (): void => {
      this.getAllProducts();
    });
  }

  render(): JSX.Element | null {
    const {
      products,
      isFiltering,
      filteredProducts,
      isLoading,
      selectedFieldsToDisplay,
      removedField,
      productCount,
      page,
    } = this.state;
    return (
      <>
        <AdvancedSearchModal
          toggleModal={this.toggleAdvancedSearchModel}
          showModal={this.state.showAdvancedSearchModal}
          model="product"
          onError={this.handleOnAdvancedSearchError}
          onData={this.handleOnAdvancedSearchData}
          triggerField={removedField?.name}
        />
          <Card>
            <div className="deparments-table-container">
              <PageToolbar modelName="products"
                searchValue={this.state.filterBy}
                onSearchChange={this.filterProducts}
                searchPlaceholder="Filter by name..."
                advancedSearch={{
                  onOpen: this.toggleAdvancedSearchModel,
                  activeFields: selectedFieldsToDisplay,
                  onFieldRemove: this.handleOnFieldRemove,
                }}
                actions={
                  <EntitledSection entitlement={entitlements.Products.create}>
                    <div style={{ display: "flex", columnGap: 8 }}>
                      <Button
                        className="base-button"
                        onClick={this.syncPrices}
                      >
                        Sync Prices
                      </Button>
                      <ExportData
                        model={"product"}
                        dataArray={this.state.products}
                      />
                    </div>
                  </EntitledSection>
                }
                primaryAction={
                  <EntitledSection entitlement={entitlements.Products.create}>
                    <Button
                      type="primary"
                      onClick={this.redirectToNewProductPage}
                      data-cy="test-new-product-button"
                    >
                      <PlusOutlined /> Create Product
                    </Button>
                  </EntitledSection>
                }
                totalCount={productCount}
                currentDataCount={isFiltering ? filteredProducts.length : products.length}
                page={page}
                pageSize={25}
              />
              {ProductTableFactory(
                isFiltering ? filteredProducts : products,
                isLoading,
                productCount,
                page,
                this.nextPage,
              )}
            </div>
          </Card>
      </>
    );
  }
}
